/**
 * Agrégation des lignes d'une réponse de requête en une valeur unique.
 *
 * Partagé par tous les widgets qui réduisent une métrique à un scalaire
 * (carte métrique, jauge, barre de progression, comparaison) : la règle
 * ratio-safe ne doit exister qu'à un seul endroit.
 */
import type { MetricField } from './fieldCatalog';

type Row = Record<string, string | number>;

function sumField(rows: Row[], field: string): number {
  return rows.reduce((acc, row) => {
    const n = Number(row[field]);
    return Number.isFinite(n) ? acc + n : acc;
  }, 0);
}

/**
 * Agrège les lignes en une valeur unique.
 *
 * Une métrique `additive` (sessions, clics, coût…) se somme. Une métrique `ratio`
 * (taux de rebond, durée moyenne, CPC…) ne se somme pas, et sa moyenne ligne à ligne
 * serait fausse — un jour à 1 session pèserait autant qu'un jour à 5 000. On la
 * recompose depuis ses composants, que le backend renvoie par ligne sous `_<nom>`
 * (voir `engine/ratios.ts`) : somme(numérateur) / somme(dénominateur).
 * Repli sur la moyenne simple si les composants sont absents de la réponse.
 */
export function aggregateMetric(rows: Row[], metric: string, def: MetricField | undefined): number {
  if (!rows.length) return 0;

  if (def?.type === 'ratio' && def.numerator && def.denominator) {
    const numerator = sumField(rows, `_${def.numerator}`);
    const denominator = sumField(rows, `_${def.denominator}`);
    if (denominator !== 0) return numerator / denominator;
  }

  const values = rows.map((row) => Number(row[metric])).filter((n) => Number.isFinite(n));
  if (!values.length) return 0;

  const sum = values.reduce((acc, n) => acc + n, 0);
  return def?.type === 'ratio' ? sum / values.length : sum;
}
